import type { SubgroupCountPublic } from "@common/client"
import { Button } from "@common/components/ui/button"
import {
  GridTable,
  GridTableCell,
  GridTableRow,
} from "@common/components/ui/grid-table"
import { LoadingSpinner } from "@common/components/ui/loading-spinner"
import { Separator } from "@common/components/ui/separator"
import { Skeleton } from "@common/components/ui/skeleton"
import { cn } from "@common/lib/utils"
import {
  createContext,
  Fragment,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react"
import {
  type Group,
  type GroupLabel,
  type GroupsOf,
  groupInOrder,
  groupRows,
  withEmptyGroups,
} from "@/lib/grouping"
import ExpandMore from "~icons/material-symbols/expand-more"
import { LoadMoreError } from "./load-more-error"
import type { GroupPaging, PagedGroup } from "./use-group-paging"

const GROUP_BAR = cn(
  "flex h-8 min-w-0 flex-1 items-center rounded-md pr-1",
  "bg-sidebar-accent/50 hover:bg-sidebar-accent",
)

const GROUP_ROW = cn(
  "min-w-0 flex-1 justify-start gap-2 rounded-md [&_svg]:size-3.5",
  "bg-transparent px-2 hover:bg-transparent",
)

const SECTION = "col-span-full grid grid-cols-subgrid"

const COLLAPSED = "invisible h-0 overflow-hidden"

const STUCK =
  "sticky top-[calc(var(--filter-bar-height)+2.5rem)] z-[5] bg-background"

const SUBGROUP_ROW = "border-b-0 hover:bg-transparent"

const SUBGROUP_CELL = "col-span-full gap-2 py-2 pl-8 pr-6"

const FOOTER_CELL = "col-span-full justify-center py-2"

const COUNT = "text-small-medium text-muted-foreground"

const REACHED_MARGIN = "100000px 0px 400px 0px"

type Grouped<R, G extends string> = {
  subgroup: G | undefined
  groupOf: (row: R, grouping: G) => GroupLabel | GroupLabel[]
  summarize?: (section: Group<R>) => React.ReactNode
  actions?: (rows: R[], section: GroupLabel) => React.ReactNode
  emptyGroups?: (grouping: G) => GroupLabel[]
  renderRows: (rows: R[], order: R[]) => React.ReactNode
  sticky?: boolean
  hideCount?: boolean
}

type Source<R> =
  | { rows: R[]; paging?: undefined }
  | { paging: GroupPaging<R>; rows?: undefined }

type Section<R> = Group<R> & {
  pending?: boolean
  subgroups?: SubgroupCountPublic[]
  page?: PagedGroup<R>
}

function sections<R>(
  source: Source<R>,
  groupOf: GroupsOf<R>,
  empty: GroupLabel[],
): Section<R>[] {
  if (!source.paging) {
    return withEmptyGroups(groupRows(source.rows, groupOf), empty)
  }
  const shown = source.paging.groups.filter(
    (page) => page.isLoading || page.isError || page.rows.length > 0,
  )
  return shown.map((page) => {
    const first = page.rows[0]
    const labels = first ? [groupOf(first)].flat() : []
    const label = labels.find((each) => each.key === page.key) ?? labels[0]
    return {
      ...(label ?? { label: "", pending: true }),
      key: page.key,
      rows: page.rows,
      total: page.count,
      subgroups: page.subgroups,
      page,
    }
  })
}

function subsections<R>(section: Section<R>, groupOf: GroupsOf<R>): Group<R>[] {
  if (!section.subgroups) return groupRows(section.rows, groupOf)
  const totals = new Map(section.subgroups.map((sub) => [sub.key, sub.count]))
  return groupInOrder(section.rows, groupOf).map((sub) => ({
    ...sub,
    total: totals.get(sub.key),
  }))
}

function sectionOrder<R, G extends string>(
  section: Section<R>,
  { subgroup, groupOf }: Pick<Grouped<R, G>, "subgroup" | "groupOf">,
): R[] {
  if (!subgroup) return section.rows
  return subsections(section, (row) => groupOf(row, subgroup)).flatMap(
    (sub) => sub.rows,
  )
}

export const GroupOpenContext = createContext(true)

export function GroupedTable<R, G extends string>({
  columns,
  style,
  header,
  group,
  ...props
}: Grouped<R, G> &
  Source<R> & {
    columns?: string
    style?: React.CSSProperties
    header: React.ReactNode
    group: G
  }): React.ReactElement {
  const [collapsed, setCollapsed] = useState<ReadonlySet<string>>(new Set())
  const keyOf = (section: Group<R>) => `${group}:${section.key}`
  const shown = sections(
    props,
    (row) => props.groupOf(row, group),
    props.emptyGroups?.(group) ?? [],
  )
  const order = [
    ...new Set(
      shown
        .filter((section) => !collapsed.has(keyOf(section)))
        .flatMap((section) => sectionOrder(section, props)),
    ),
  ]

  const setOpen = (key: string, open: boolean) => {
    const next = new Set(collapsed)
    if (open) next.delete(key)
    else next.add(key)
    setCollapsed(next)
  }

  return (
    <GridTable className={columns} style={style}>
      {header}
      {shown.map((section) => (
        <GroupSection
          key={keyOf(section)}
          section={section}
          {...props}
          open={!collapsed.has(keyOf(section))}
          onOpenChange={(open) => setOpen(keyOf(section), open)}
          renderRows={(sectionRows) => props.renderRows(sectionRows, order)}
        />
      ))}
    </GridTable>
  )
}

function GroupSection<R, G extends string>({
  section,
  open,
  onOpenChange,
  subgroup,
  groupOf,
  summarize,
  actions,
  renderRows,
  sticky,
  hideCount,
  paging,
}: Omit<Grouped<R, G>, "renderRows"> & {
  section: Section<R>
  paging?: GroupPaging<R>
  open: boolean
  onOpenChange: (open: boolean) => void
  renderRows: (rows: R[]) => React.ReactNode
}): React.ReactElement {
  const barRef = useReached(true, () => paging?.reveal(section.key))

  return (
    <div className={SECTION}>
      <div
        className={cn(
          "col-span-full flex items-center px-2 py-1",
          sticky && STUCK,
        )}
      >
        <div ref={barRef} className={GROUP_BAR}>
          <Button
            type="button"
            variant="ghost"
            className={GROUP_ROW}
            aria-expanded={open}
            onClick={() => onOpenChange(!open)}
          >
            <ExpandMore
              className={cn(
                "text-muted-foreground transition-transform",
                !open && "-rotate-90",
              )}
            />
            {section.icon}
            {section.pending ? (
              <Skeleton className="h-4 w-24" />
            ) : (
              <GroupTitle label={section.label} detail={section.detail} />
            )}
            {!hideCount && (
              <span className={COUNT}>
                {section.total ?? section.rows.length}
              </span>
            )}
            {summarize && (
              <span className={cn(COUNT, "ml-auto tabular-nums")}>
                {summarize(section)}
              </span>
            )}
          </Button>
          {actions?.(section.rows, section)}
        </div>
      </div>
      <div className={cn(SECTION, !open && COLLAPSED)}>
        <GroupOpenContext.Provider value={open}>
          {subgroup
            ? subsections(section, (row) => groupOf(row, subgroup)).map(
                (sub) => (
                  <Fragment key={sub.key}>
                    <SubgroupRow
                      label={sub.label}
                      detail={sub.detail}
                      icon={sub.icon}
                      count={hideCount ? null : (sub.total ?? sub.rows.length)}
                    />
                    {renderRows(sub.rows)}
                  </Fragment>
                ),
              )
            : renderRows(section.rows)}
          {section.page && paging && (
            <GroupFooter page={section.page} paging={paging} />
          )}
        </GroupOpenContext.Provider>
      </div>
    </div>
  )
}

export function scrollParent(element: HTMLElement): HTMLElement | null {
  const parent = element.parentElement
  if (!parent) return null
  const { overflowY } = getComputedStyle(parent)
  return overflowY === "auto" || overflowY === "scroll"
    ? parent
    : scrollParent(parent)
}

function useReached(
  enabled: boolean,
  onReach: () => void,
): React.RefObject<HTMLDivElement | null> {
  const ref = useRef<HTMLDivElement>(null)
  const reachRef = useRef(onReach)
  reachRef.current = onReach

  useEffect(() => {
    const element = ref.current
    if (!element || !enabled) return
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) reachRef.current()
      },
      { root: scrollParent(element), rootMargin: REACHED_MARGIN },
    )
    observer.observe(element)
    return () => observer.disconnect()
  }, [enabled])

  return ref
}

function GroupFooter<R>({
  page,
  paging,
}: {
  page: PagedGroup<R>
  paging: GroupPaging<R>
}): React.ReactElement | null {
  const open = useContext(GroupOpenContext)
  const ready = !page.isLoading && !page.isError && page.remaining > 0
  const sentinelRef = useReached(open && ready, () => paging.showMore(page.key))

  if (page.isError) {
    return (
      <FooterRow>
        <LoadMoreError
          message="Couldn't load this group."
          onRetry={() => paging.retry(page.key)}
        />
      </FooterRow>
    )
  }
  if (!page.isLoading && page.remaining === 0) return null
  return (
    <FooterRow>
      <div ref={sentinelRef} className="flex h-5 justify-center">
        {page.isLoading && <LoadingSpinner className="size-5" />}
      </div>
    </FooterRow>
  )
}

function FooterRow({
  children,
}: {
  children: React.ReactNode
}): React.ReactElement {
  return (
    <GridTableRow className={SUBGROUP_ROW}>
      <GridTableCell className={FOOTER_CELL}>{children}</GridTableCell>
    </GridTableRow>
  )
}

function GroupTitle({
  label,
  detail,
}: {
  label: string
  detail: string | undefined
}): React.ReactElement {
  return (
    <>
      <span className={detail ? "text-body-semibold" : "text-body-medium"}>
        {label}
      </span>
      {detail && (
        <span className="truncate text-body-medium text-muted-foreground">
          {detail}
        </span>
      )}
    </>
  )
}

function SubgroupRow({
  label,
  detail,
  icon,
  count,
}: {
  label: string
  detail: string | undefined
  icon: React.ReactNode
  count: number | null
}): React.ReactElement {
  return (
    <GridTableRow className={SUBGROUP_ROW}>
      <GridTableCell className={SUBGROUP_CELL}>
        {icon ?? (
          <span className="size-1 shrink-0 rounded-full bg-muted-foreground" />
        )}
        <GroupTitle label={label} detail={detail} />
        {count !== null && <span className={COUNT}>{count}</span>}
        <Separator className="ml-2 w-auto flex-1" />
      </GridTableCell>
    </GridTableRow>
  )
}
